import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";

import { LanguageProvider, useI18n, SUPPORTED } from "@/i18n";
import HomePage from "@/pages/HomePage";
import Prototype from "@/pages/Prototype";

import { AuthProvider, ProtectedRoute } from "@/saas/AuthContext";
import Login from "@/saas/Login";
import Register from "@/saas/Register";
import ChangePassword from "@/saas/ChangePassword";
import CustomerLayout from "@/saas/CustomerLayout";
import Dashboard from "@/saas/Dashboard";
import NewProject from "@/saas/NewProject";
import ProjectDetail from "@/saas/ProjectDetail";
import Account from "@/saas/Account";
import Billing from "@/saas/Billing";
import CheckoutSuccess from "@/saas/CheckoutSuccess";
import CheckoutCancel from "@/saas/CheckoutCancel";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Cookies from "@/pages/legal/Cookies";
import LegalNotice from "@/pages/legal/LegalNotice";
import DataProcessing from "@/pages/legal/DataProcessing";

import AdminLayout from "@/saas/admin/AdminLayout";
import AdminOverview from "@/saas/admin/AdminOverview";
import AdminCustomers from "@/saas/admin/AdminCustomers";
import AdminProjects from "@/saas/admin/AdminProjects";
import AdminCredits from "@/saas/admin/AdminCredits";
import AdminMessages from "@/saas/admin/AdminMessages";
import AdminSettings from "@/saas/admin/AdminSettings";
import AdminDiagnostics from "@/saas/admin/AdminDiagnostics";

const AGENCY = ["AGENCY_ADMIN", "AGENCY_USER"];
const OWNER = ["OWNER_ADMIN"];

// Public language-prefixed routes (/nl /en /fr /es): sync the chosen language, then render.
function LangHome({ lang }) {
  const { setLang } = useI18n();
  useEffect(() => { if (SUPPORTED.includes(lang)) setLang(lang); }, [lang, setLang]);
  return <HomePage />;
}

// Language-prefixed legal pages (e.g. /fr/terms) — same pages, language synced from the URL.
function LangLegal({ lang, children }) {
  const { setLang } = useI18n();
  useEffect(() => { if (SUPPORTED.includes(lang)) setLang(lang); }, [lang, setLang]);
  return children;
}

const LEGAL_ROUTES = [
  { path: "/terms", el: <Terms /> },
  { path: "/privacy", el: <Privacy /> },
  { path: "/cookies", el: <Cookies /> },
  { path: "/legal", el: <LegalNotice /> },
  { path: "/data-processing", el: <DataProcessing /> },
];

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Routes>
            {/* Public marketing site */}
            <Route path="/" element={<HomePage />} />
            {SUPPORTED.map((l) => (
              <Route key={l} path={`/${l}`} element={<LangHome lang={l} />} />
            ))}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Public legal / compliance pages (unprefixed URLs stay canonical for NL) */}
            {LEGAL_ROUTES.map((r) => (
              <Route key={r.path} path={r.path} element={r.el} />
            ))}
            {SUPPORTED.map((l) =>
              LEGAL_ROUTES.map((r) => (
                <Route key={`${l}${r.path}`} path={`/${l}${r.path}`} element={<LangLegal lang={l}>{r.el}</LangLegal>} />
              ))
            )}

            {/* Stripe Checkout return pages (informational only — no credit/activation) */}
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />

            {/* Forced / self-service password change (any authenticated role) */}
            <Route path="/account/password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

            {/* Customer area */}
            <Route element={<ProtectedRoute roles={AGENCY}><CustomerLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/projects/new" element={<NewProject />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/account" element={<Account />} />
              <Route path="/account/billing" element={<Billing />} />
            </Route>

            {/* Owner admin area */}
            <Route element={<ProtectedRoute roles={OWNER}><AdminLayout /></ProtectedRoute>}>
              <Route path="/admin" element={<AdminOverview />} />
              <Route path="/admin/customers" element={<AdminCustomers />} />
              <Route path="/admin/projects" element={<AdminProjects />} />
              <Route path="/admin/credits" element={<AdminCredits />} />
              <Route path="/admin/messages" element={<AdminMessages />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/diagnostics" element={<AdminDiagnostics />} />
            </Route>

            {/* Legacy owner-only technical fallback */}
            <Route path="/prototype" element={<ProtectedRoute roles={OWNER}><Prototype /></ProtectedRoute>} />
          </Routes>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
